import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RoleGate } from "@/components/auth/RoleGate";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { customersService } from "@/services/customers.service";
import { customerSegmentsService } from "@/services/customerSegments.service";
import { getErrorMessage } from "@/services/api";
import { UF_OPTIONS } from "@/config/constants";
import { ManageSegmentsDialog } from "@/features/customers/components/ManageSegmentsDialog";
import { Settings2 } from "lucide-react";

const schema = z.object({
    legal_name: z.string().min(2, "Informe a razão social"),
    trade_name: z.string().optional().nullable(),
    tax_id: z.string().optional().nullable(),
    main_contact: z.string().optional().nullable(),
    phone: z.string().optional().nullable(),
    whatsapp: z.string().optional().nullable(),
    email: z.string().email("E-mail inválido").optional().or(z.literal("")).nullable(),
    address: z.string().optional().nullable(),
    city: z.string().optional().nullable(),
    state: z.string().optional().nullable(),
    zip_code: z.string().optional().nullable(),
    business_segment: z.string().optional().nullable(),
    notes: z.string().optional().nullable(),
});

function Field({ label, error, children, className = "" }) {
    return (
        <div className={`space-y-1.5 ${className}`}>
            <Label className="text-sm font-medium">{label}</Label>
            {children}
            {error && <div className="text-xs text-danger">{error}</div>}
        </div>
    );
}

export default function CustomerFormPage() {
    const { id } = useParams();
    const isEdit = !!id;
    const navigate = useNavigate();
    const qc = useQueryClient();

    const detail = useQuery({
        queryKey: ["customer", id],
        queryFn: () => customersService.get(id),
        enabled: isEdit,
    });

    const segmentsQ = useQuery({
        queryKey: ["customer-segments", { includeInactive: false }],
        queryFn: () => customerSegmentsService.list(),
        staleTime: 30_000,
    });
    const activeSegments = segmentsQ.data?.items || [];
    const [manageSegmentsOpen, setManageSegmentsOpen] = React.useState(false);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            legal_name: "",
            trade_name: "",
            tax_id: "",
            main_contact: "",
            phone: "",
            whatsapp: "",
            email: "",
            address: "",
            city: "",
            state: "",
            zip_code: "",
            business_segment: "",
            notes: "",
        },
    });

    React.useEffect(() => {
        if (isEdit && detail.data) reset({ ...detail.data, notes: detail.data.notes || "" });
    }, [isEdit, detail.data, reset]);

    const save = useMutation({
        mutationFn: (data) => {
            const payload = { ...data };
            Object.keys(payload).forEach((k) => {
                if (payload[k] === "") payload[k] = null;
            });
            return isEdit ? customersService.update(id, payload) : customersService.create(payload);
        },
        onSuccess: () => {
            toast.success(isEdit ? "Cliente atualizado." : "Cliente criado.");
            qc.invalidateQueries({ queryKey: ["customers"] });
            qc.invalidateQueries({ queryKey: ["customer", id] });
            navigate("/customers");
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao salvar cliente.")),
    });

    const [deleteOpen, setDeleteOpen] = React.useState(false);
    const deleteMut = useMutation({
        mutationFn: () => customersService.remove(id),
        onSuccess: () => {
            toast.success("Cliente excluído.");
            qc.invalidateQueries({ queryKey: ["customers"] });
            navigate("/customers", { replace: true });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao excluir cliente.")),
    });

    return (
        <form onSubmit={handleSubmit(save.mutate)} className="space-y-6">
            <PageHeader
                title={isEdit ? "Editar cliente" : "Novo cliente"}
                description="Dados cadastrais e contato do cliente."
                backTo="/customers"
                breadcrumb={[
                    { label: "Início", to: "/dashboard" },
                    { label: "Clientes", to: "/customers" },
                    { label: isEdit ? "Editar" : "Novo" },
                ]}
                actions={
                    <>
                        {isEdit && (
                            <RoleGate resource="customers" action="delete">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-500/50 dark:text-red-300 dark:hover:bg-red-500/10"
                                    onClick={() => setDeleteOpen(true)}
                                    data-testid="customer-delete-button"
                                >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Excluir
                                </Button>
                            </RoleGate>
                        )}
                        <Button type="button" variant="ghost" onClick={() => navigate("/customers")}>
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className="bg-secondary text-secondary-foreground hover:opacity-90"
                            disabled={save.isPending}
                            data-testid="customer-save-button"
                        >
                            {save.isPending ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <Save className="mr-2 h-4 w-4" />
                            )}
                            Salvar
                        </Button>
                    </>
                }
            />

            <div className="space-y-6">
                <Card className="rounded-xl border shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Dados gerais</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                        <Field label="Razão Social *" error={errors.legal_name?.message} className="md:col-span-2">
                            <Input {...register("legal_name")} data-testid="customer-legal-name" />
                        </Field>
                        <Field label="Nome Fantasia">
                            <Input {...register("trade_name")} />
                        </Field>
                        <Field label="CNPJ / CPF">
                            <Input {...register("tax_id")} data-testid="customer-tax-id" />
                        </Field>
                        <Field label="Segmento">
                            <div className="flex gap-2">
                                <Select
                                    value={watch("business_segment") || ""}
                                    onValueChange={(v) => setValue("business_segment", v)}
                                >
                                    <SelectTrigger data-testid="customer-segment" className="flex-1">
                                        <SelectValue placeholder="Selecione" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {activeSegments.length === 0 && (
                                            <div className="px-3 py-2 text-xs text-muted-foreground">
                                                Nenhum segmento cadastrado.
                                            </div>
                                        )}
                                        {activeSegments.map((s) => (
                                            <SelectItem key={s.id} value={s.name}>
                                                {s.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <RoleGate resource="customers" action="create">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        title="Gerenciar segmentos"
                                        onClick={() => setManageSegmentsOpen(true)}
                                        data-testid="manage-segments-button"
                                    >
                                        <Settings2 className="h-4 w-4" />
                                    </Button>
                                </RoleGate>
                            </div>
                        </Field>
                        <Field label="Contato principal">
                            <Input {...register("main_contact")} />
                        </Field>
                    </CardContent>
                </Card>

                <Card className="rounded-xl border shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Contato</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                        <Field label="Telefone">
                            <Input {...register("phone")} />
                        </Field>
                        <Field label="WhatsApp">
                            <Input {...register("whatsapp")} />
                        </Field>
                        <Field label="E-mail" error={errors.email?.message} className="md:col-span-2">
                            <Input type="email" {...register("email")} />
                        </Field>
                    </CardContent>
                </Card>

                <Card className="rounded-xl border shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Endereço</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                        <Field label="Endereço" className="md:col-span-2">
                            <Input {...register("address")} />
                        </Field>
                        <Field label="Cidade">
                            <Input {...register("city")} />
                        </Field>
                        <Field label="UF">
                            <Select value={watch("state") || ""} onValueChange={(v) => setValue("state", v)}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                    {UF_OPTIONS.map((uf) => (
                                        <SelectItem key={uf} value={uf}>
                                            {uf}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="CEP">
                            <Input {...register("zip_code")} />
                        </Field>
                    </CardContent>
                </Card>

                <Card className="rounded-xl border shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Notas</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Textarea rows={4} {...register("notes")} placeholder="Observações adicionais…" />
                    </CardContent>
                </Card>
            </div>

            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Excluir cliente?</AlertDialogTitle>
                        <AlertDialogDescription>
                            O cliente será marcado como excluído. Ordens de serviço, equipamentos e histórico permanecerão preservados, mas não será mais possível criar novos vínculos com este cliente.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="customer-delete-cancel">Manter</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); deleteMut.mutate(); }}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="customer-delete-confirm"
                        >
                            Excluir cliente
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <ManageSegmentsDialog
                open={manageSegmentsOpen}
                onOpenChange={setManageSegmentsOpen}
            />
        </form>
    );
}
